import { refresh } from '@api/authentication';
import { getServicability } from '@api/store';
import { TAddon, TAddress, TAmenity, TCartItem, TData, TImage, TInventory, TOption, TOrderItem, TPricingPlan, TStore, TStoreMetaV2, TStoreTiming } from '@components/types';
import type { Tag } from '@tryghost/content-api';
import Compressor from 'compressorjs';
import { getDistance } from 'geolib';
import Cookies from 'js-cookie';
import moment from 'moment';
import { ImageLoaderProps } from 'next/image';
import router from 'next/router';

export const requestLogin = (next: string) => {
    localStorage.removeItem("token");
    if (Cookies.get('refresh_token')?.length > 0) {
        refresh().then((res) => {
            localStorage.setItem("token", res.access_token);
        }).catch((e) => {
            console.debug("Error while refresh request");
            console.debug(e);
        })
        router.push(`/login?next=${next}`);
    }
}

export const getTemplateUrl = (url: string) => {
    url =(`https://business.jhattse.com/api/template${url}`);
    return url;
}

export const sanityIoImageLoader = ({ src, width, quality }: ImageLoaderProps) => {
    if (src.startsWith("http")) {
        return `https://cdn.jhattse.com/resize?width=${width}&quality=${quality || 75}&type=webp&url=${encodeURIComponent(src)}`
    } else {
        return `https://cdn.jhattse.com/resize?width=${width}&file=${src}&quality=${quality || 75}&type=webp`
    }
}

// Shown when an image source is missing or unusable ("", "undefined", "null").
export const FALLBACK_IMAGE_URL = "https://cdn.jhattse.com/public/consumer/placeholder/product_square.png";

const isUsableImageSrc = (src?: string | null) => !!src && src !== "undefined" && src !== "null";

// Wraps an image loader so a missing/invalid src resolves to a placeholder instead of building a broken
// resize URL (".../resize?file=undefined") that fails to load and stretches its container.
export const withSafeSrc = (loader: (props: ImageLoaderProps) => string, fallback: string = FALLBACK_IMAGE_URL) =>
    (props: ImageLoaderProps) =>
        loader(isUsableImageSrc(props.src) ? props : { ...props, src: fallback });

export const safeImageLoader = withSafeSrc(sanityIoImageLoader);

export const staticImageLoader = ({ src, width, quality }: ImageLoaderProps) => {
    if (src.startsWith("http")) {
        return `${src}`
    } else {
        return `https://cdn.jhattse.com/${src}`
    }
}

export const getFilteredResults = (input: string, data: TData[]) => {
    if (data == undefined) {
        return new Array<TOption>();
    }
    const inputValue = input != undefined ? input.trim().toLowerCase() : undefined;
    const inputLength = inputValue == undefined ? 0 : inputValue.length;

    const filteredResults = inputLength === 0 ? data : data.filter(store =>
        store?.name?.toLowerCase().slice(0, inputLength) === inputValue
    );

    const subFilteredResults = inputLength === 0 ? data : data.filter(store =>
        store?.name?.toLowerCase().includes(inputValue)
    );
    let result = [];
    if (filteredResults?.length > 5) {
        result = filteredResults;
    } else {
        result = Array.from(new Set([...filteredResults, ...subFilteredResults]));
    }
    return result.map((store) => { return { value: store?.id?.toString(), label: store.name } });
};


export const getSafeUrl = (name: string) => {
    return (name || '')?.trim().replace(/ +/g, '-').replace(/[.,\?:&\+\/%\|]+/g, '-').replace(/-+/g, '-').toLowerCase();
}

export const range = (start: number, stop: number, step: number) =>
    Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + (i * step))

export const checkLocalhost = () => { return typeof (window) !== "undefined" && window.location.hostname.includes("localhost") }
export const humanizeCurrency = (x: number) => { return x?.toLocaleString("en-IN", { style: "currency", currency: "INR" }).split('.')[0] }
export const humanizeNumber = (x: number) => { return x?.toLocaleString('en-IN') }
export const getBusinessUrl = (url: string) => {
    return checkLocalhost() ? url : `https://business.jhattse.com${url}`
}

export const getImageUrl = (productImages: TImage[]) => {
    return (productImages || [])?.length > 0 ? productImages[0].url : "https://jhattse.com/public/consumer/placeholder/product_square.png";
}

export const getSortedImages = (productImages: TImage[]) => {
    if ((productImages || [])?.length > 0) {
        const images = [...productImages?.filter((x) => x.type === "main"), ...productImages?.filter((x) => x.type !== "main")]
        return images.sort((a, b) => a.id - b.id)
    } else {
        return [{ url: "https://jhattse.com/public/consumer/placeholder/product_square.png", description: "No image available", id: 0, product_id: 0, type: "other" } as TImage]
    }
}

export const getImageObject = (productImages: TImage[], placeholderImage: string = "https://jhattse.com/public/consumer/placeholder/product_square.png") => {
    if ((productImages || [])?.length > 0) {
        const images = [...productImages.filter((x) => x.type === "main"), ...productImages.filter((x) => x.type !== "main")]
        return images.sort((a, b) => a.id - b.id)[0]
    }
    else {
        return { url: placeholderImage || "https://jhattse.com/public/consumer/placeholder/product_square.png", description: "No image available", id: 0, product_id: 0, type: "other" } as TImage
    }
}

export const calculateTax = (orderItems: TOrderItem[]) => {
    return Array.from(orderItems)?.reduce((prevTax, currentTax) => prevTax + (currentTax?.total_tax || 0), 0)
}

export const calculateCost = (orderItems: TOrderItem[]) => {
    return Array.from(orderItems)?.reduce((prevCost, currentCost) => prevCost + (currentCost?.total_cost || 0), 0)
}

export function groupBy<T>(arr: T[], fn: (item: T) => any) {
    return arr?.reduce<Record<string, T[]>>((prev, curr) => {
        const groupKey = fn(curr);
        const group = prev[groupKey] || [];
        group.push(curr);
        return { ...prev, [groupKey]: group };
    }, {});
}

export const trimToLength = (name: string, charLength: number) => {
    return name?.length > charLength ? `${name?.slice(0, charLength)}...` : name;
}


export const getColor = (rating?: number) => {
    if (rating === undefined) {
        return 1
    }
    return rating < 2.5 ? 1 : (rating < 4 ? 2 : (rating < 4.5 ? 3 : 3))
}

export const getLength = (iter: Array<any> | undefined | null) => {
    if (iter === null || iter === undefined) {
        return 0
    } else {
        return iter.length;
    }
}

export function getFirst<Type>(iter: Type[] | undefined | null): Type {
    if (iter === null || iter === undefined || iter.length === 0) {
        return {} as Type
    } else {
        return iter[0];
    }
}

export function suggestColor(num: number, isText: boolean = false) {
    if (num > 4) return isText ? 'text-success-500' : 'bg-success-500';
    else if (num > 3) return isText ? 'text-success-300' : 'bg-success-300';
    else if (num > 2) return isText ? 'text-yellow-400' : 'bg-yellow-400';
    else if (num > 1) return isText ? 'text-yellow-300' : 'bg-yellow-300';
    else return isText ? 'text-error-300' : 'bg-error-300';
}

export function formatDate(date: string) {
    return moment.utc(date).format('DD/MM/YYYY');
}

export function formatPhone(phone: string, defaultCC = "91") {
    if (!phone) return "";

    // Keep digits only
    let digits = phone.replace(/\D/g, "");

    // If starts with +country code → return digits without "+"
    if (phone.trim().startsWith("+")) {
        return digits;
    }

    // If already starts with the default country code → return as-is
    if (digits.startsWith(defaultCC) || digits.length > 10) {
        return digits;
    }

    // Otherwise prepend country code
    return defaultCC + digits;
}

export const PriceCalculation = (component: TCartItem) => {
    // All Required Element
    let addOnPrice = component?.addon?.filter((a) => a?.enable != false)?.reduce((prev: number, addon) => prev + (addon?.price || addon?.inventory?.price || 0), 0)
    let price = ((component?.inventory?.price || component?.inventory?.mrp || component?.product?.mrp) + (addOnPrice || 0));
    return (price * component?.quantity)?.toFixed(2)
}

export const modalPriceCalculation = (inventory: TInventory, quantity: number, addons: TAddon[]) => {
    let addOnPrice = addons?.reduce((prev: number, addon) => prev + (addon?.price || addon?.inventory?.price || 0), 0)
    let price = ((inventory?.price || inventory?.mrp) + (addOnPrice || 0));
    return (price * quantity)?.toFixed(2)
}

export const formatAddress = (address: TAddress, placeholder: string = "") => {
    const fullAddress = [address?.house_number, address?.street_name, address?.locality, address?.city?.name, address?.state?.name, address?.pincode].filter(element => { return element?.length > 0; }).join(', ');
    return fullAddress?.length > 0 ? fullAddress : placeholder;
}

export const getTags = (tags: Tag[]) => {
    return tags.filter((tag: Tag) => !["ABOUT", "BUSINESS", "STORY", "BRAND", "Brand", "ANNOUNCEMENT"].includes(tag.name))
}

// Within this distance (or in the same city) a visitor counts as "near" a pickup-only store.
const NEARBY_PICKUP_KM = 30;

// Formats an opening-hours time ("09:00:00", with or without fractional seconds). Returns "" for
// missing/unparseable values (e.g. closed days) so callers never render "Invalid date".
export const formatStoreTime = (time?: string): string => {
    if (!time) return "";
    const parsed = moment(time, ["HH:mm:ss.SSSSSS", "HH:mm:ss", "HH:mm"]);
    return parsed.isValid() ? parsed.format("hh:mm a") : "";
}

export const hasStoreHours = (timing?: TStoreTiming): boolean =>
    !!formatStoreTime(timing?.open_time) && !!formatStoreTime(timing?.close_time);

const toMinutes = (time?: string): number | null => {
    const parsed = moment(time, ["HH:mm:ss.SSSSSS", "HH:mm:ss", "HH:mm"]);
    return time && parsed.isValid() ? parsed.hours() * 60 + parsed.minutes() : null;
}

// Hours are stored as plain times. open == close (e.g. 00:00 - 00:00) means open all day, and a
// close at/before the open (e.g. 08:00 - 00:00) means the store closes after midnight.
export const isTimingOpenNow = (timing?: TStoreTiming, now = moment()): boolean => {
    const open = toMinutes(timing?.open_time);
    const close = toMinutes(timing?.close_time);
    if (open === null || close === null) return false;
    const current = now.hours() * 60 + now.minutes();
    if (open === close) return true;
    return open < close ? current >= open && current < close : current >= open || current < close;
}

export const formatStoreHours = (timing?: TStoreTiming): string => {
    const open = toMinutes(timing?.open_time);
    const close = toMinutes(timing?.close_time);
    if (open === null || close === null) return "";
    if (open === close) return "Open 24 hours";
    return `${formatStoreTime(timing.open_time)} - ${close === 0 ? "12:00 am (midnight)" : formatStoreTime(timing.close_time)}`;
}

// A store with no timings configured is treated as open, since we can't tell otherwise.
export const isStoreOpenNow = (timings: TStoreTiming[] = []): boolean => {
    if (!(timings?.length > 0)) return true;
    return timings.some((t) => t?.day_of_week === new Date().getDay() && hasStoreHours(t) && isTimingOpenNow(t));
}

export const isStoreNearUser = (store: TStore, userLocation: any): boolean => {
    const userCityId = userLocation?.Location?.city_id || userLocation?.Location?.id;
    const storeCityId = store?.address?.city_id || (store?.address?.city as any)?.id;
    if (userCityId && storeCityId && userCityId === storeCityId) return true;
    const { latitude: uLat, longitude: uLon } = userLocation?.Coords || {};
    const { latitude: sLat, longitude: sLon } = store?.address || {};
    if (uLat != null && uLon != null && sLat != null && sLon != null) {
        return getDistance({ latitude: uLat, longitude: uLon }, { latitude: sLat, longitude: sLon }) <= NEARBY_PICKUP_KM * 1000;
    }
    return false;
}

// Whether the visitor can order from this store right now:
//  - pickup-only stores: only when the visitor's city/location is near the store
//  - category 1 stores: only when the store delivers to the visitor's city
//  - every other category sells shippable products, so ordering depends on opening hours, not delivery
export const checkStoreAvailablity = async (store: TStore, storeTimings: TStoreTiming[] = []): Promise<boolean> => {
    let userLocation = null;
    try {
        userLocation = JSON.parse(localStorage.getItem("location"));
    } catch { }
    const isPickupOnly = store?.is_pickup === true && !store?.is_delivery;
    if (isPickupOnly) {
        return isStoreNearUser(store, userLocation) && (store?.category_id === 1 || isStoreOpenNow(storeTimings));
    }
    if (store?.category_id !== 1) {
        return isStoreOpenNow(storeTimings);
    }
    const res = await getServicability([store?.id], userLocation?.Location?.city_id || userLocation?.Location?.id);
    return res?.length > 0;
}

export function capitalizeString(str: string) {
    if (typeof str !== 'string' || str.length === 0) {
        return str; // return the string as is if it's not a valid string or it's empty
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

// Get Store Details Functions
export const getStoreForItem = (cartItem: TCartItem, stores: TStore[] = []) => {
    if (!cartItem?.inventory?.store_id && !(stores?.length > 0)) return null;
    return stores[cartItem?.inventory?.store_id];
};

export const getHoursDifference = (time: string): number => {
    const givenTime = moment(time);
    const currentTime = moment();
    return currentTime?.diff(givenTime, "hours");
};

// GST slab on room tariff — kept identical to the backend's get_gst_rate (crud/booking/utils.py):
// <=7500 -> 5%, >7500 -> 18%. Only a fallback: prices shown to guests come from the server's
// stay quote (`quotes` on /bookings/engine/schedule), which is what checkout charges.
export const roomGstRate = (nightlyPrice: number) =>
    nightlyPrice <= 7500 ? 0.05 : 0.18;

// Backend room prices are pre-tax. Returns the tax-inclusive nightly price when `applyGst`
// is on (stores with gst_billing); otherwise the price is returned unchanged.
export const withRoomGst = (nightlyPrice: number, applyGst: boolean = true) =>
    applyGst && nightlyPrice > 0 ? Math.round(nightlyPrice * (1 + roomGstRate(nightlyPrice)) * 100) / 100 : nightlyPrice;

export type TStayPricing = {
    nights: number;
    // Pre-tax rate for each night of the stay, one room.
    nightlyBase: number[];
    // Totals for the whole stay and the given room count.
    base: number;
    tax: number;
    total: number;
    // Tax-inclusive average per night, one room (what the card headline shows).
    avgNightly: number;
};

// Turns the server's stay quote (one room, whole stay, one plan) into the card's pricing for
// `roomCount` rooms. This is the authoritative path; getStayPricing below is the fallback.
// Occupancy settings of a room category (assetgroup.info) — mirrors get_occupancy_rules in the
// backend (crud/booking/utils.py). Children under 12 don't count towards capacity.
export type TOccupancyRules = { base: number; max: number; extraAdultPrice: number; extraChildPrice: number; isDorm: boolean };
export const getOccupancyRules = (info?: any, capacity?: number): TOccupancyRules => {
    const num = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
    const base = Math.trunc(num(info?.guest_capacity)) || Math.trunc(num(capacity)) || 2;
    const max = Math.max(Math.trunc(num(info?.max_guest_capacity)) || base, base);
    return {
        base, max,
        extraAdultPrice: Math.max(num(info?.extra_adult_price), 0),
        extraChildPrice: Math.max(num(info?.extra_child_price), 0),
        isDorm: !!info?.is_dorm,
    };
};

// Units a party needs (mirrors rooms_needed_for_party): adults vs max per room; dorms per bed.
export const roomsNeededForParty = (adults: number, rules: TOccupancyRules) =>
    rules.isDorm ? Math.max(adults || 0, 1) : Math.max(Math.ceil(Math.max(adults || 0, 1) / rules.max), 1);

// Extra adults/children across `rooms` units (mirrors extra_guests): adults fill the included
// places first, children use what's left; anyone beyond pays the extra-guest rate.
export const extraGuests = (adults: number, children: number, rooms: number, rules: TOccupancyRules) => {
    if (rules.isDorm || rooms <= 0) return { extraAdults: 0, extraChildren: 0 };
    const included = rules.base * rooms;
    const extraAdults = Math.max((adults || 0) - included, 0);
    const freePlaces = Math.max(included - (adults || 0), 0);
    return { extraAdults, extraChildren: Math.max((children || 0) - freePlaces, 0) };
};

type TQuoteLike = {
    plan_id?: number; nights: number; total_cost: number; total_tax: number; total_payable: number; avg_nightly_payable: number;
    tax_rate?: number; rooms?: number; occupancy?: number | null; party_total_cost?: number; party_total_tax?: number; party_total_payable?: number;
    extra_adult_cost_per_night?: number; extra_child_cost_per_night?: number;
};

// Turns the server's stay quote into the card's pricing for `roomCount` rooms. When the room count
// is what the party needs, the server's party total (rooms + extra-guest charges) is used as-is;
// for another count the extras are re-worked with the server's per-person rates and tax rate,
// exactly as checkout will. getStayPricing below is only the fallback when there's no quote.
export const pricingFromQuote = (
    quotes: TQuoteLike[] | undefined,
    planId: number | undefined,
    roomCount: number = 1,
    party?: { adults: number; children: number; rules: TOccupancyRules } | null,
): TStayPricing | null => {
    const quote = (planId ? quotes?.find((q) => q?.plan_id === planId) : undefined) || (!planId ? quotes?.[0] : undefined);
    if (!quote || !(quote.total_payable > 0)) return null;
    const qty = Math.max(1, roomCount || 1);
    const round2 = (n: number) => Math.round(n * 100) / 100;
    if (party && quote.rooms === qty && quote.party_total_payable > 0) {
        return {
            nights: quote.nights, nightlyBase: [],
            base: round2(quote.party_total_cost), tax: round2(quote.party_total_tax), total: round2(quote.party_total_payable),
            avgNightly: round2(quote.party_total_payable / qty / quote.nights),
        };
    }
    let extrasCost = 0;
    if (party) {
        // An occupancy plan (SG/TW/TR/QD) already covers its own guests — same as rules_for_plan.
        const rules = quote.occupancy ? { ...party.rules, base: Math.max(party.rules.base, quote.occupancy) } : party.rules;
        const { extraAdults, extraChildren } = extraGuests(party.adults, party.children, qty, rules);
        extrasCost = (extraAdults * (quote.extra_adult_cost_per_night || 0) + extraChildren * (quote.extra_child_cost_per_night || 0)) * quote.nights;
    }
    const base = quote.total_cost * qty + extrasCost;
    const tax = quote.total_tax * qty + extrasCost * ((quote.tax_rate || 0) / 100);
    return {
        nights: quote.nights, nightlyBase: [],
        base: round2(base), tax: round2(tax), total: round2(base + tax),
        avgNightly: round2((base + tax) / qty / quote.nights),
    };
};

// Fallback when the server quote is missing: prices a stay from the per-date availability.
// GST is applied per night here; the server averages the stay first, so prefer pricingFromQuote.
export const getStayPricing = (
    periods: { date?: string; price?: number; min_price?: number; prices?: { plan_id?: number; price?: number }[] }[] | undefined,
    planId: number | undefined,
    startDate: string,
    endDate: string,
    applyGst: boolean,
    fallbackNightly?: number,
    roomCount: number = 1,
): TStayPricing | null => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const nights = Math.max(1, end.diff(start, 'days') || 1);
    const startKey = start.format('YYYY-MM-DD');
    const endKey = end.format('YYYY-MM-DD');

    // Nights are the dates in [check-in, check-out); fall back to the response order if dates are missing.
    const inStay = (periods || []).filter((p) => {
        const d = p?.date ? moment(p.date).format('YYYY-MM-DD') : null;
        return d ? d >= startKey && d < endKey : false;
    });
    const source = (inStay.length ? inStay : (periods || [])).slice(0, nights);

    const rateFor = (p: (typeof source)[number]) =>
        (planId ? p?.prices?.find((x) => x?.plan_id === planId)?.price : undefined)
        || p?.prices?.[0]?.price || p?.price || p?.min_price || 0;

    let nightlyBase = source.map(rateFor).filter((r) => r > 0);
    if (nightlyBase.length < nights) {
        // Some nights missing from the response: fill them with the known rate (or the fallback).
        const filler = nightlyBase[nightlyBase.length - 1] || fallbackNightly || 0;
        if (!(filler > 0)) return null;
        nightlyBase = [...nightlyBase, ...Array(nights - nightlyBase.length).fill(filler)];
    }

    const qty = Math.max(1, roomCount || 1);
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const base = round2(nightlyBase.reduce((s, r) => s + r, 0) * qty);
    const total = round2(nightlyBase.reduce((s, r) => s + withRoomGst(r, applyGst), 0) * qty);
    return {
        nights,
        nightlyBase,
        base,
        tax: round2(total - base),
        total,
        avgNightly: round2(total / qty / nights),
    };
};

// Rate/meal plans the booking engine offers for one room category and the searched party.
// /bookings/engine/schedule is the only source that knows them: each quote and each per-date
// price carries plan_id + plan_name + plan_code. The global /pricingplans catalogue is paginated
// and property-agnostic, so plan ids can't be resolved through it. `prices` (assetgroup.prices,
// whose rows embed `pricing_plan`) only fills in a name/description the schedule left out.
export const getOfferedPlans = (
    available?: {
        quotes?: { plan_id?: number; plan_name?: string; plan_code?: string }[];
        periods?: { prices?: { plan_id?: number; plan_name?: string; plan_code?: string }[] }[];
    } | null,
    prices?: { plan_id?: number; pricing_plan?: { id?: number; name?: string; description?: string } }[],
): TPricingPlan[] => {
    const configured = new Map<number, { name?: string; description?: string }>();
    for (const price of prices || []) {
        const id = price?.pricing_plan?.id || price?.plan_id;
        if (id > 0 && !configured.has(id)) {
            configured.set(id, { name: price?.pricing_plan?.name, description: price?.pricing_plan?.description });
        }
    }

    const offered = new Map<number, TPricingPlan>();
    const add = (id?: number, name?: string, code?: string) => {
        if (!(id > 0) || offered.has(id)) return;
        offered.set(id, {
            id,
            name: name || configured.get(id)?.name || code || `Plan ${id}`,
            code,
            description: configured.get(id)?.description,
        });
    };

    // Quotes are already narrowed to the party (occupancy plans SG/TW/TR/QD), so they win.
    // Without them, fall back to the first night's rate list.
    if (available?.quotes?.length) {
        for (const quote of available.quotes) add(quote?.plan_id, quote?.plan_name, quote?.plan_code);
    } else {
        for (const price of available?.periods?.[0]?.prices || []) add(price?.plan_id, price?.plan_name, price?.plan_code);
    }
    return Array.from(offered.values());
};

// One-line cancellation summary for a room card. A property-written policy is shown as-is (first
// line); otherwise the platform default (free until 7 days before arrival) is turned into a dated line.
export const getCancellationSummary = (customPolicy: string | undefined, startDate: string): string => {
    const custom = customPolicy?.trim();
    if (custom) {
        const firstLine = custom.split(/\r?\n/).find((l) => l.trim())?.trim() || custom;
        return firstLine.length > 110 ? `${firstLine.slice(0, 107).trim()}…` : firstLine;
    }
    const checkIn = moment(startDate).startOf('day');
    const today = moment().startOf('day');
    const freeUntil = checkIn.clone().subtract(7, 'days');
    if (today.isBefore(freeUntil) || today.isSame(freeUntil)) {
        return `Free cancellation until ${freeUntil.format('D MMM YYYY')}`;
    }
    const halfUntil = checkIn.clone().subtract(5, 'days');
    if (today.isBefore(halfUntil) || today.isSame(halfUntil)) return '50% charged if cancelled now; non-refundable within 5 days of arrival';
    return 'Non-refundable for these dates';
};

// `ref` on a stay link records the listing/search page the guest came from, so the stay page can
// offer an "All Hotels" link back to that exact page (keeping ?q= / city). Only same-site /stays
// paths are accepted; anything else (other origins, protocol-relative URLs) is dropped.
export const sanitizeStaysRef = (value?: string | string[] | null): string | null => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw || typeof raw !== 'string') return null;
    if (!/^\/stays(?:\/in\/[^/?#]+)?(?:\?[^#]*)?$/.test(raw) || raw.startsWith('//') || raw.includes('\\')) return null;
    return raw;
};

// Amenity ids chosen by the property. The API stores them in storemeta.settings.amenities;
// storemeta.amenities is an older path kept as a fallback.
export const getStoreAmenityIds = (store?: { storemeta?: any } | null): number[] => {
    const ids = store?.storemeta?.settings?.amenities ?? store?.storemeta?.amenities ?? [];
    return (Array.isArray(ids) ? ids : []).map((id: string | number) => Number(id)).filter((id: number) => id > 0);
};

// Canonical path of a city's stays page. A curated city (CITY_META) whose configured id matches the
// real city id resolves from the slug alone; every other city only loads with ?city_id=, so the
// parameter is part of its canonical URL.
export const getCityStaysPath = (cityName: string, cityId: number, curatedCityId?: number) =>
    `/stays/in/${getSafeUrl(cityName).replace(/\s+/g, '-').toLowerCase()}-hotels${curatedCityId === cityId ? '' : `?city_id=${cityId}`}`;

export const getCheckInTimeFormat = (storeMeta: TStoreMetaV2) => {
    return storeMeta?.settings?.check_in_time || "HH:mm:ss"
}

export const getCheckOutTimeFormat = (storeMeta: TStoreMetaV2) => {
    return storeMeta?.settings?.check_out_time || "23:59:59"
}

export const toTitleCase = (str: string) => {
    return str?.toLowerCase()?.split(' ')?.map(function (word: string) {
        return (word?.charAt(0)?.toUpperCase() + word?.slice(1));
    }).join(' ');
}

// Image Compressor Fun
export const imageCompressorFunc = (file: File, maxSizeMB = 1): Promise<File> => {
    if (!file) {
        return Promise.reject(new Error("No file provided"));
    }
    // Skip compression for non-image files or already small files
    if (!file.type.startsWith('image/') || file.size <= maxSizeMB * 1024 * 1024) {
        return Promise.resolve(file);
    }
    const MAX_SIZE_BYTES = maxSizeMB * 1024 * 1024;
    // Calculate appropriate quality and dimensions based on original file size
    const sizeRatio = file.size / MAX_SIZE_BYTES;
    const quality = Math.max(0.1, Math.min(0.7, 1 / (sizeRatio * 0.8))); // Adaptive quality
    // Set reasonable max dimensions for product images
    const maxWidth = 1200;
    const maxHeight = 1200;

    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality,
            maxWidth: 1200,
            maxHeight: 1200,
            // Use WebP for better compression
            mimeType: 'image/webp',
            checkOrientation: true,
            success: result => {
                const compressedFile = new File([result],
                    file.name.replace(/\.[^/.]+$/, ".webp"),
                    {
                        type: 'image/webp',
                        lastModified: Date.now()
                    }
                );
                resolve(compressedFile);
            },
            error: reject
        });
    });
};

export const validatePhone = (phone: string, optional: boolean = false) => {
    if (optional) {
        if (phone) {
            let phoneRegex = new RegExp(/^(\+91)?(0)?\d{10}$/);
            return phoneRegex.test(phone)
        } else return true
    } else {
        let phoneRegex = new RegExp(/^(\+91)?(0)?\d{10}$/);
        return phoneRegex.test(phone)
    }
}

export const getAddress = (address: TAddress, placeholder: string = "", full: boolean = true) => {
    const fullAddress = (full ? [address?.house_number, address?.street_name, address?.locality, address?.city?.name, address?.state?.name, address?.pincode] : [address?.city?.name, address?.state?.name]).filter(element => { return element?.length > 0; }).join(', ');
    return fullAddress?.length > 0 ? fullAddress : placeholder;
}

export const isValidGSTIN = (gstin: string): boolean => {
    if (gstin?.length !== 15) return false;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstin.toUpperCase());
};

export const allAmenities: TAmenity[] = [
    // 🛏️ Room / Stay Amenities
    { id: 1, name: "Air Conditioning", category: "Room", icon: "Wind" },
    { id: 2, name: "Heating", category: "Room", icon: "Thermometer" },
    { id: 3, name: "Fan", category: "Room", icon: "Fan" },
    { id: 4, name: "Bed", category: "Room", icon: "Bed" },
    { id: 5, name: "Extra Bed", category: "Room", icon: "BedDouble" },
    { id: 6, name: "Sofa Bed", category: "Room", icon: "Sofa" },
    { id: 7, name: "Wardrobe / Closet", category: "Room", icon: "DoorClosed" },
    { id: 8, name: "Dressing Table", category: "Room", icon: "Table" },
    { id: 9, name: "Desk / Work Table", category: "Room", icon: "Laptop" },
    { id: 10, name: "Chair", category: "Room", icon: "Armchair" },
    { id: 11, name: "Mirror", category: "Room", icon: "Square" },
    { id: 12, name: "Blackout Curtains", category: "Room", icon: "Moon" },
    { id: 13, name: "Soundproof Rooms", category: "Room", icon: "VolumeX" },
    { id: 14, name: "Carpeted Floor", category: "Room", icon: "Layers" },
    { id: 15, name: "Wooden Flooring", category: "Room", icon: "Grid" },

    // 🚿 Bathroom Amenities
    { id: 16, name: "Attached Bathroom", category: "Bathroom", icon: "Bath" },
    { id: 17, name: "Hot Water", category: "Bathroom", icon: "Droplets" },
    { id: 18, name: "Shower", category: "Bathroom", icon: "ShowerHead" },
    { id: 19, name: "Bathtub", category: "Bathroom", icon: "Bath" },
    { id: 20, name: "Jacuzzi", category: "Bathroom", icon: "Waves" },
    { id: 21, name: "Towels", category: "Bathroom", icon: "Layers" },
    { id: 22, name: "Bathrobes", category: "Bathroom", icon: "Shirt" },
    { id: 23, name: "Slippers", category: "Bathroom", icon: "Footprints" },
    { id: 24, name: "Toiletries", category: "Bathroom", icon: "Soap" },
    { id: 25, name: "Hair Dryer", category: "Bathroom", icon: "Wind" },
    { id: 26, name: "Shaving Kit", category: "Bathroom", icon: "Scissors" },
    { id: 27, name: "Dental Kit", category: "Bathroom", icon: "Smile" },
    { id: 28, name: "Western Toilet", category: "Bathroom", icon: "Toilet" },
    { id: 29, name: "Bidet", category: "Bathroom", icon: "Droplet" },
    { id: 30, name: "Geyser", category: "Bathroom", icon: "Flame" },
    { id: 31, name: "24x7 Water Supply", category: "Bathroom", icon: "Droplets" },

    // 📺 Entertainment & Electronics
    { id: 32, name: "Television", category: "Entertainment", icon: "Tv" },
    { id: 33, name: "Smart TV", category: "Entertainment", icon: "Monitor" },
    { id: 34, name: "Cable TV", category: "Entertainment", icon: "Cable" },
    { id: 35, name: "Streaming Apps", category: "Entertainment", icon: "PlayCircle" },
    { id: 36, name: "Wi-Fi", category: "Entertainment", icon: "Wifi" },
    { id: 37, name: "High-Speed Internet", category: "Entertainment", icon: "SignalHigh" },
    { id: 38, name: "Telephone", category: "Entertainment", icon: "Phone" },
    { id: 39, name: "Music System", category: "Entertainment", icon: "Music" },
    { id: 40, name: "Bluetooth Speaker", category: "Entertainment", icon: "Speaker" },
    { id: 41, name: "Gaming Console", category: "Entertainment", icon: "Gamepad2" },

    // 🍽️ Food & Kitchen
    { id: 42, name: "Kitchen", category: "Kitchen", icon: "ChefHat" },
    { id: 43, name: "Kitchenette", category: "Kitchen", icon: "Utensils" },
    { id: 44, name: "Refrigerator", category: "Kitchen", icon: "Refrigerator" },
    { id: 45, name: "Microwave", category: "Kitchen", icon: "Microwave" },
    { id: 46, name: "Oven", category: "Kitchen", icon: "Microwave" },
    { id: 47, name: "Stove / Induction", category: "Kitchen", icon: "Flame" },
    { id: 48, name: "Electric Kettle", category: "Kitchen", icon: "Kettle" },
    { id: 49, name: "Coffee Maker", category: "Kitchen", icon: "Coffee" },
    { id: 50, name: "Tea Maker", category: "Kitchen", icon: "CupSoda" },
    { id: 51, name: "Toaster", category: "Kitchen", icon: "Sandwich" },
    { id: 52, name: "Cookware", category: "Kitchen", icon: "Utensils" },
    { id: 53, name: "Cutlery", category: "Kitchen", icon: "UtensilsCrossed" },
    { id: 54, name: "Dining Table", category: "Kitchen", icon: "Table" },
    { id: 55, name: "Room Service", category: "Food", icon: "Bell" },
    { id: 56, name: "Breakfast Included", category: "Food", icon: "Coffee" },
    { id: 57, name: "Mini Bar", category: "Food", icon: "Wine" },

    // 🧹 Cleaning & Laundry
    { id: 58, name: "Housekeeping", category: "Cleaning & Laundry", icon: "Broom" },
    { id: 59, name: "Laundry Service", category: "Cleaning & Laundry", icon: "Shirt" },
    { id: 60, name: "Daily Cleaning", category: "Cleaning & Laundry", icon: "CalendarCheck" },
    { id: 61, name: "Washing Machine", category: "Cleaning & Laundry", icon: "WashingMachine" },
    { id: 62, name: "Iron", category: "Cleaning & Laundry", icon: "Zap" },
    { id: 63, name: "Ironing Board", category: "Cleaning & Laundry", icon: "RectangleHorizontal" },
    { id: 64, name: "Dry Cleaning", category: "Cleaning & Laundry", icon: "Droplets" },
    { id: 65, name: "Linen Change", category: "Cleaning & Laundry", icon: "Layers" },

    // 🔐 Safety
    { id: 66, name: "CCTV", category: "Safety", icon: "Cctv" },
    { id: 67, name: "Security Guard", category: "Safety", icon: "ShieldCheck" },
    { id: 68, name: "Safe Locker", category: "Safety", icon: "Lock" },
    { id: 69, name: "Fire Extinguisher", category: "Safety", icon: "Flame" },
    { id: 70, name: "Smoke Detector", category: "Safety", icon: "AlertTriangle" },
    { id: 71, name: "First Aid Kit", category: "Safety", icon: "HeartPulse" },
    { id: 72, name: "Emergency Exit", category: "Safety", icon: "DoorOpen" },
    { id: 73, name: "Door Lock", category: "Safety", icon: "Key" },
    { id: 74, name: "Intercom", category: "Safety", icon: "PhoneCall" },

    // 🏊 Recreation & Wellness
    { id: 75, name: "Swimming Pool", category: "Recreation", icon: "Waves" },
    { id: 76, name: "Indoor Pool", category: "Recreation", icon: "Droplets" },
    { id: 77, name: "Gym / Fitness Center", category: "Recreation", icon: "Dumbbell" },
    { id: 78, name: "Yoga Room", category: "Recreation", icon: "Lotus" },
    { id: 79, name: "Spa", category: "Recreation", icon: "Flower2" },
    { id: 80, name: "Sauna", category: "Recreation", icon: "ThermometerSun" },
    { id: 81, name: "Steam Room", category: "Recreation", icon: "Cloud" },
    { id: 82, name: "Massage", category: "Recreation", icon: "Hand" },
    { id: 83, name: "Game Room", category: "Recreation", icon: "Gamepad2" },
    { id: 84, name: "Table Tennis", category: "Recreation", icon: "CircleDot" },
    { id: 85, name: "Billiards", category: "Recreation", icon: "Circle" },
    { id: 86, name: "Kids Play Area", category: "Recreation", icon: "Baby" },
    { id: 87, name: "Garden", category: "Recreation", icon: "Leaf" },
    { id: 88, name: "Lawn", category: "Recreation", icon: "Table" },

    // 🚗 Parking & Transport
    { id: 89, name: "Free Parking", category: "Parking & Transport", icon: "ParkingSquare" },
    { id: 90, name: "Paid Parking", category: "Parking & Transport", icon: "ParkingCircle" },
    { id: 91, name: "Valet Parking", category: "Parking & Transport", icon: "Car" },
    { id: 92, name: "EV Charging", category: "Parking & Transport", icon: "BatteryCharging" },
    { id: 93, name: "Bicycle Rental", category: "Parking & Transport", icon: "Bike" },
    { id: 94, name: "Car Rental", category: "Parking & Transport", icon: "CarFront" },
    { id: 95, name: "Airport Pickup", category: "Parking & Transport", icon: "PlaneLanding" },
    { id: 96, name: "Airport Drop", category: "Parking & Transport", icon: "PlaneTakeoff" },
    { id: 97, name: "Shuttle Service", category: "Parking & Transport", icon: "Bus" },

    // 🧳 Guest Services (FIXED IDS)
    { id: 98, name: "24x7 Front Desk", category: "Guest Services", icon: "Clock" },
    { id: 99, name: "Luggage Storage", category: "Guest Services", icon: "Luggage" },
    { id: 100, name: "Bellboy Service", category: "Guest Services", icon: "Bell" },
    { id: 101, name: "Wake-up Call", category: "Guest Services", icon: "AlarmClock" },
    { id: 102, name: "Tour Assistance", category: "Guest Services", icon: "MapPinned" },

    // ♿ Accessibility
    { id: 103, name: "Wheelchair Accessible", category: "Accessibility", icon: "Wheelchair" },
    { id: 104, name: "Elevator / Lift", category: "Accessibility", icon: "ArrowUpDown" },
    { id: 105, name: "Ramp Access", category: "Accessibility", icon: "TrendingUp" },
    { id: 106, name: "Accessible Bathroom", category: "Accessibility", icon: "Bath" },
    { id: 107, name: "Braille Signage", category: "Accessibility", icon: "Eye" },

    // 🐶 Pet & Family Friendly
    { id: 108, name: "Pet Friendly", category: "Family", icon: "PawPrint" },
    { id: 109, name: "Kids Friendly", category: "Family", icon: "Baby" },
    { id: 110, name: "Crib / Baby Cot", category: "Family", icon: "Bed" },
    { id: 111, name: "High Chair", category: "Family", icon: "Armchair" },
    { id: 112, name: "Babysitting", category: "Family", icon: "UserCheck" },
    { id: 113, name: "Play Area", category: "Family", icon: "Gamepad2" },

    // 🏢 Business & Events
    { id: 114, name: "Business Center", category: "Business", icon: "Briefcase" },
    { id: 115, name: "Meeting Room", category: "Business", icon: "Users" },
    { id: 116, name: "Conference Hall", category: "Business", icon: "Presentation" },
    { id: 117, name: "Printer", category: "Business", icon: "Printer" },
    { id: 118, name: "Scanner", category: "Business", icon: "ScanLine" },
    { id: 119, name: "Projector", category: "Business", icon: "Projector" },
    { id: 120, name: "Whiteboard", category: "Business", icon: "Clipboard" },

    // 🌿 Outdoor & Miscellaneous
    { id: 121, name: "Balcony", category: "Outdoor", icon: "Sun" },
    { id: 122, name: "Terrace", category: "Outdoor", icon: "Mountain" },
    { id: 127, name: "Barbecue", category: "Outdoor", icon: "FlameKindling" },
    { id: 128, name: "Bonfire", category: "Outdoor", icon: "Flame" },
    { id: 123, name: "Mountain View", category: "View", icon: "MountainSnow" },
    { id: 124, name: "Sea View", category: "View", icon: "Waves" },
    { id: 125, name: "City View", category: "View", icon: "Building2" },
    { id: 126, name: "Fireplace", category: "Misc", icon: "Flame" },
    { id: 129, name: "Smoking Area", category: "Misc", icon: "Cigarette" },
    { id: 130, name: "Non-Smoking Rooms", category: "Misc", icon: "Ban" }

];

export const calculateNights = (startDate: string, endDate: string) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const totalSeconds = end.diff(start, 'seconds');
    return Math.max(Math.round((totalSeconds || 0) / 86400), 1);
}

export const checkIsInventoryUnavailable = (inventory: TInventory) => {
    return ((!inventory?.is_available && (inventory as any)?.meta?.continue_offline !== true) || (!(inventory?.quantity > 0) && (!inventory?.always_available)));
}

export const getAvailableInventoryQuantity = (inventory?: TInventory) => Number(inventory?.quantity || 0);

export const getCartQuantityForInventory = (cart: Map<string, TCartItem>, inventoryId?: number, excludeKeys: string[] = []) => {
    return Array.from(cart?.entries() || []).reduce((total, [key, item]) => {
        if (excludeKeys.includes(key) || (item as any)?.deleted === true || item?.inventory?.id !== inventoryId) return total;
        return total + (Number(item?.quantity) || 0);
    }, 0);
}

export const isStockQuantityExceeded = (cart: Map<string, TCartItem>, inventory?: TInventory, quantity: number = 0, excludeKeys: string[] = []) => {
    if (inventory?.always_available === true) return false;
    const existingQuantity = getCartQuantityForInventory(cart, inventory?.id, excludeKeys);
    return existingQuantity + (Number(quantity) || 0) > getAvailableInventoryQuantity(inventory);
}

export const stockQuantityErrorMessage = (inventory?: TInventory) => `Only ${getAvailableInventoryQuantity(inventory)} quantity available.`;
